<script lang="ts">
  // The 2x2 viewing grid. Each cell has a camera picker and a video tile.
  // The 4-slot selection is loaded from and saved back to the server.
  import { onMount } from "svelte";
  import { listCameras, getLayout, saveLayout, type Camera } from "./api";
  import VideoCell from "./VideoCell.svelte";

  let cameras: Camera[] = [];
  let slots: (string | null)[] = [null, null, null, null];
  let loaded = false;
  let error = "";

  onMount(async () => {
    try {
      [cameras, slots] = await Promise.all([listCameras(), getLayout()]);
    } catch (e) {
      error = String(e).replace(/^Error:\s*/, "");
    } finally {
      loaded = true;
    }
  });

  function nameFor(slug: string | null): string {
    return cameras.find((c) => c.slug === slug)?.name ?? "";
  }

  // Single-flight save: only one PUT in flight at a time, and if the user
  // changes a slot while a save is running we re-save the latest snapshot
  // afterwards. This prevents two concurrent PUTs committing out of order.
  let saveInFlight = false;
  let savePending = false;

  async function flushSave(): Promise<void> {
    if (saveInFlight) { savePending = true; return; }
    saveInFlight = true;
    try {
      do {
        savePending = false;
        await saveLayout(slots);
      } while (savePending);
    } catch (e) {
      error = String(e).replace(/^Error:\s*/, "");
    } finally {
      saveInFlight = false;
    }
  }

  function pick(i: number, slug: string): void {
    slots[i] = slug || null;
    slots = slots; // trigger reactivity
    flushSave();
  }
</script>

<div class="grid">
  {#each slots as slug, i}
    <div class="grid-cell">
      <div class="cell-bar">
        <select value={slug ?? ""} on:change={(e) => pick(i, e.currentTarget.value)}>
          <option value="">— select camera —</option>
          {#each cameras as cam}
            <option value={cam.slug}>{cam.name}</option>
          {/each}
        </select>
      </div>
      {#key slug}
        <VideoCell {slug} name={nameFor(slug)} />
      {/key}
    </div>
  {/each}
</div>

{#if loaded && cameras.length === 0}
  <div class="grid-hint">No cameras yet. An admin can add them under “Admin”.</div>
{/if}
{#if error}<div class="grid-hint err">{error}</div>{/if}
